/* MaisonVera room selection. Existing product forms remain independent. */
(function (host) {
  'use strict';

  /** @param {unknown} value @returns {string} */
  function id(value) {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new Error('Invalid identifier');
    const result = String(value);
    if (!/^[1-9]\d*$/.test(result)) throw new Error('Invalid identifier');
    return result;
  }

  /** Compare native option labels without treating typography as a size change. @param {unknown} value @returns {string} */
  function variantTitleIdentity(value) {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Variant title unavailable');
    return value.normalize('NFKC').toLowerCase().trim().replace(/(\d)\s*[×✕x]\s*(?=\d)/g, '$1x').replace(/(\d)\s+cm\b/g, '$1cm').replace(/\s+/g, ' ');
  }

  /** Match only an explicit two-dimensional nominal size. @param {object} variant @param {string} size @returns {boolean} */
  function hasNominalSize(variant, size) {
    return [variant.title, variant.option1, variant.option2, variant.option3].some(value => {
      if (typeof value !== 'string' || !value.trim()) return false;
      const normalized = variantTitleIdentity(value);
      return Array.from(normalized.matchAll(/(?:^|[^\da-z])(\d{2,3})x(\d{2,3})(?:cm)?(?=$|[^\da-z])/g), match => match[1] + 'x' + match[2] + 'cm').includes(size);
    });
  }

  /** @param {string} route @param {string} origin @returns {URL} */
  function localUrl(route, origin) {
    if (typeof route !== 'string' || !route.trim()) throw new Error('Invalid room route');
    const url = new URL(route, origin);
    if (url.origin !== new URL(origin).origin || url.username || url.password) throw new Error('Invalid room route');
    return url;
  }

  /** @param {object} crypto @returns {string} */
  function actionId(crypto) {
    if (crypto?.randomUUID) return crypto.randomUUID();
    if (!crypto?.getRandomValues) throw new Error('Random action identifier unavailable');
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  }

  /** Validate a real cart before using any of its contents as proof. @param {object} cart @returns {object} */
  function checkedCart(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    for (const line of cart.items) {
      id(line.variant_id ?? line.id);
      if (!Number.isSafeInteger(line.quantity) || line.quantity < 0) throw new Error('Invalid cart quantity');
      if (line.properties != null && (typeof line.properties !== 'object' || Array.isArray(line.properties))) throw new Error('Invalid cart properties');
    }
    return cart;
  }

  /** @param {object} action @param {object} cart @returns {object} */
  function reconcile(action, cart) {
    checkedCart(cart);
    const actual = new Map();
    let unexpected = false;
    for (const line of cart.items) {
      if (line.properties?._maisonvera_room_action !== action.token) continue;
      if (line.properties?._maisonvera_room !== action.room) unexpected = true;
      const variantId = id(line.variant_id ?? line.id);
      const quantity = (actual.get(variantId) || 0) + line.quantity;
      if (!Number.isSafeInteger(quantity)) throw new Error('Cart quantity overflow');
      actual.set(variantId, quantity);
      if (!action.expected.some(item => item.id === variantId)) unexpected = true;
    }
    const missing = [];
    let added = 0;
    for (const item of action.expected) {
      const quantity = actual.get(item.id) || 0;
      added += quantity;
      if (quantity > item.quantity) unexpected = true;
      if (quantity < item.quantity) missing.push({id: item.id, title: item.title, variantTitle: item.variantTitle, quantity: item.quantity - quantity});
    }
    return {exact: !unexpected && missing.length === 0, unexpected, missing, added};
  }

  /**
   * All mutations are confined to one explicit, frozen add action. GET requests are
   * authoritative; a failed POST is never assumed to mean that nothing changed.
   * @param {object} config Liquid-generated room contract.
   * @param {object} dependencies Browser APIs or offline test doubles.
   */
  class RoomCartEngine {
    constructor(config, dependencies = {}) {
      if (!config || !Array.isArray(config.items) || !config.items.length || typeof config.key !== 'string' || !config.key) throw new Error('Invalid room data');
      if (config.behaviorVersion !== undefined && ![1, 2].includes(config.behaviorVersion)) throw new Error('Unsupported room behavior');
      this.behaviorVersion = config.behaviorVersion || 1;
      this.config = config;
      this.origin = dependencies.origin || host.location?.origin;
      if (!this.origin) throw new Error('Room origin unavailable');
      this.fetch = dependencies.fetch || host.fetch?.bind(host);
      if (!this.fetch) throw new Error('Room fetch unavailable');
      this.makeActionId = dependencies.makeActionId || (() => actionId(host.crypto));
      this.onState = dependencies.onState || (() => {});
      this.timeout = dependencies.timeout ?? 15000;
      this.controllers = new Set();
      this.usedActionIds = new Set();
      this.products = new Map();
      this.state = 'idle';
      this.busy = false;
      this.action = null;
      this.destroyed = false;
      this.items = [];
      const keys = new Set();
      const products = new Set();
      for (const source of config.items) {
        if (typeof source.key !== 'string' || !source.key || keys.has(source.key)) throw new Error('Duplicate room item');
        const productId = id(source.productId);
        if (products.has(productId) || typeof source.handle !== 'string' || !source.handle || /[/?#]/.test(source.handle)) throw new Error('Invalid room product');
        keys.add(source.key);
        products.add(productId);
        const allowed = (source.allowedVariantIds || []).map(id);
        this.items.push({...source, productId, allowedVariantIds: allowed});
      }
      this.readRequiredKeys = undefined;
      if (this.behaviorVersion === 2) {
        const required = config.readRequiredKeys;
        if (!Array.isArray(required) || required.length !== 1 || !keys.has(required[0]) || this.items.find(item => item.key === required[0]).productId !== id(config.productId)) throw new Error('Room initial read must identify its anchor');
        this.readRequiredKeys = [...required];
      }
      this.routes = {};
      for (const key of ['root', 'cartAdd', 'cartRead', 'cartPage']) this.routes[key] = localUrl(config.routes?.[key], this.origin);
      if (!config.routes?.root || !config.routes?.cartAdd || !config.routes?.cartRead || !config.routes?.cartPage) throw new Error('Room routes missing');
      this.rules = config.compatibility || [];
      if (!Array.isArray(this.rules)) throw new Error('Invalid room compatibility');
      for (const rule of this.rules) {
        if (!keys.has(rule.left) || !keys.has(rule.right) || rule.left === rule.right || !Array.isArray(rule.pairs)) throw new Error('Invalid room rule');
        for (const pair of rule.pairs) {
          if (!Array.isArray(pair) || pair.length !== 2) throw new Error('Invalid room variant pair');
          id(pair[0]);
          id(pair[1]);
        }
      }
      this.choiceGroups = config.choiceGroups === undefined ? [] : config.choiceGroups;
      if (!Array.isArray(this.choiceGroups)) throw new Error('Invalid room choice groups');
      this.groupForItem = new Map();
      const groupKeys = new Set();
      for (const group of this.choiceGroups) {
        if (!group || typeof group.key !== 'string' || !/^[a-z][a-z0-9_-]*$/.test(group.key) || groupKeys.has(group.key) || !keys.has(group.anchor) || !Array.isArray(group.itemKeys) || !group.itemKeys.length || ![0, 1].includes(group.min) || group.max !== 1) throw new Error('Invalid exclusive room group');
        groupKeys.add(group.key);
        const memberKeys = new Set();
        for (const key of group.itemKeys) {
          if (typeof key !== 'string' || !/^[a-z][a-z0-9_-]*$/.test(key) || !keys.has(key) || key === group.anchor || memberKeys.has(key) || this.groupForItem.has(key)) throw new Error('Invalid room group member');
          memberKeys.add(key);
          const rules = this.rules.filter(rule => rule.left === group.anchor && rule.right === key);
          if (rules.length !== 1 || !rules[0].pairs.length) throw new Error('Room model needs exact compatibility');
          const variantIds = itemKey => {
            const item = this.items.find(value => value.key === itemKey);
            const renderedIds = (item.variants || []).map(variant => id(variant.id));
            const allowedIds = item.allowedVariantIds.length ? item.allowedVariantIds : renderedIds;
            if (new Set(allowedIds).size !== allowedIds.length || allowedIds.some(value => !renderedIds.includes(value))) throw new Error('Room model variants differ from rendered product');
            return new Set(allowedIds);
          };
          const anchorIds = variantIds(group.anchor);
          const memberIds = variantIds(key);
          const pairs = new Set();
          for (const pair of rules[0].pairs) {
            const left = id(pair[0]);
            const right = id(pair[1]);
            const pairKey = left + ':' + right;
            if (!anchorIds.has(left) || !memberIds.has(right) || pairs.has(pairKey)) throw new Error('Room model pair differs from its products');
            pairs.add(pairKey);
          }
          this.groupForItem.set(key, group);
        }
      }
      for (const group of this.choiceGroups) if (this.groupForItem.has(group.anchor)) throw new Error('Room choice anchor cannot be optional');
      this.reviewedVariantTitles = new Map();
      this.reviewedVariantIdentities = new Map();
      const reviewedKeys = this.behaviorVersion === 2 ? keys : new Set(this.choiceGroups.flatMap(group => [group.anchor, ...group.itemKeys]));
      for (const key of reviewedKeys) {
        const item = this.items.find(value => value.key === key);
        const titles = new Map();
        const identities = new Map();
        for (const variant of item.variants || []) {
          const variantId = id(variant.id);
          if (item.allowedVariantIds.length && !item.allowedVariantIds.includes(variantId)) continue;
          if (titles.has(variantId)) throw new Error('Duplicate reviewed model variant');
          titles.set(variantId, variantTitleIdentity(variant.title));
          if (this.behaviorVersion === 2) {
            const identity = variant.identity;
            if (!identity || typeof identity !== 'object' || Array.isArray(identity) || typeof identity.sku !== 'string' || Object.keys(identity).some(field => !['sku', 'size'].includes(field)) || (identity.size !== undefined && (typeof identity.size !== 'string' || !/^\d{2,3}x\d{2,3}cm$/.test(identity.size) || !hasNominalSize(variant, identity.size)))) throw new Error('Invalid reviewed variant identity');
            identities.set(variantId, Object.freeze({...identity}));
          }
        }
        if (this.behaviorVersion === 2 && (!titles.size || new Set(item.allowedVariantIds).size !== item.allowedVariantIds.length || item.allowedVariantIds.some(variantId => !titles.has(variantId)))) throw new Error('Room variants differ from rendered product');
        this.reviewedVariantTitles.set(key, titles);
        this.reviewedVariantIdentities.set(key, identities);
      }
      if (this.behaviorVersion === 2) {
        for (const rule of this.rules) {
          const pairs = new Set();
          if (!rule.pairs.length) throw new Error('Empty reviewed compatibility');
          for (const pair of rule.pairs) {
            const pairKey = id(pair[0]) + ':' + id(pair[1]);
            if (!this.reviewedVariantTitles.get(rule.left).has(id(pair[0])) || !this.reviewedVariantTitles.get(rule.right).has(id(pair[1])) || pairs.has(pairKey)) throw new Error('Room pair differs from rendered variants');
            const leftSize = this.reviewedVariantIdentities.get(rule.left).get(id(pair[0])).size;
            const rightSize = this.reviewedVariantIdentities.get(rule.right).get(id(pair[1])).size;
            if ((leftSize !== undefined || rightSize !== undefined || this.groupForItem.has(rule.right)) && (!leftSize || leftSize !== rightSize)) throw new Error('Room pair has unverified nominal sizes');
            pairs.add(pairKey);
          }
        }
      }
    }

    /** @param {string} state */
    setState(state) {
      this.state = state;
      // Presentation failures must never skip cart reconciliation or unlock a POST.
      try { this.onState(state); } catch (_) { /* The action state remains authoritative. */ }
    }

    get locked() {
      return this.action !== null;
    }

    /** @param {URL} url @param {object} options @param {boolean} json @returns {Promise<object>} */
    async request(url, options = {}, json = false) {
      if (this.destroyed) throw new Error('Room disconnected');
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), this.timeout);
      try {
        const response = await this.fetch(url.href, {...options, credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal, headers: {Accept: 'application/json', ...options.headers}});
        if (!json) return response;
        if (!response.ok) throw new Error('Room read failed');
        return await response.json();
      } finally {
        clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    /** @returns {Promise<object>} */
    async readCart() {
      const cart = checkedCart(await this.request(this.routes.cartRead, {}, true));
      if (cart.currency !== this.config.currency || !Number.isSafeInteger(cart.item_count) || cart.items.reduce((sum, line) => sum + line.quantity, 0) !== cart.item_count) throw new Error('Cart currency or completeness changed');
      return cart;
    }

    /** @param {Array<string>|undefined} requiredKeys @returns {Promise<void>} */
    async refreshProducts(requiredKeys) {
      const required = new Set(requiredKeys || this.readRequiredKeys || this.items.filter(item => !this.groupForItem.has(item.key)).map(item => item.key));
      const results = await Promise.allSettled(this.items.map(async item => {
        const root = new URL(this.routes.root);
        if (!root.pathname.endsWith('/')) root.pathname += '/';
        const url = new URL('products/' + encodeURIComponent(item.handle) + '.js', root);
        const product = await this.request(url, {}, true);
        if (id(product.id) !== item.productId || product.handle !== item.handle || !Array.isArray(product.variants)) throw new Error('Product identity differs');
        const variants = [];
        const seen = new Set();
        for (const source of product.variants) {
          const variantId = id(source.id);
          if (seen.has(variantId) || !Number.isSafeInteger(source.price) || source.price < 0 || typeof source.available !== 'boolean') throw new Error('Invalid product variant');
          seen.add(variantId);
          if (item.allowedVariantIds.length && !item.allowedVariantIds.includes(variantId)) continue;
          const reviewedTitles = this.reviewedVariantTitles.get(item.key);
          if (reviewedTitles && (!reviewedTitles.has(variantId) || reviewedTitles.get(variantId) !== variantTitleIdentity(source.title))) throw new Error('Reviewed model variant identity changed');
          const identity = this.reviewedVariantIdentities.get(item.key)?.get(variantId);
          if (identity && ((typeof source.sku !== 'string' && source.sku !== null) || (source.sku ?? '') !== identity.sku || (identity.size !== undefined && !hasNominalSize(source, identity.size)))) throw new Error('Reviewed variant identity changed');
          variants.push({...source, id: variantId});
        }
        return [item.key, {...product, variants}];
      }));
      const next = new Map();
      let failed = false;
      for (const [index, result] of results.entries()) {
        if (result.status === 'rejected') {
          if (required.has(this.items[index].key)) failed = true;
        } else next.set(...result.value);
      }
      // Optional unreadable products must not poison other choices or retain stale data.
      if (this.behaviorVersion === 2 || this.choiceGroups.length) this.products = next;
      if (failed || this.destroyed) throw new Error('Room product reads incomplete');
      this.products = next;
    }

    /** @returns {Promise<object>} */
    async initialize() {
      if (this.busy || this.locked) return {kind: 'locked'};
      this.busy = true;
      this.setState('loading');
      try {
        await this.refreshProducts();
        this.setState('ready');
        return {kind: 'ready'};
      } catch (_) {
        this.setState('idle');
        return {kind: 'readFailed'};
      } finally {
        this.busy = false;
      }
    }

    /** @param {string} key @param {string} variantId @returns {object|undefined} */
    variant(key, variantId) {
      return this.products.get(key)?.variants.find(value => value.id === String(variantId));
    }

    /** @param {string} key @param {string} anchorVariantId @returns {Array<object>} */
    matchingVariants(key, anchorVariantId) {
      const variants = this.products.get(key)?.variants || [];
      const group = this.groupForItem.get(key);
      if (!group) return variants;
      const rule = this.rules.find(value => value.left === group.anchor && value.right === key);
      return variants.filter(variant => rule.pairs.some(pair => String(pair[0]) === String(anchorVariantId) && String(pair[1]) === variant.id));
    }

    /** @param {Array<object>} selections @returns {object} */
    validate(selections) {
      if (!Array.isArray(selections) || !selections.length) return {valid: false, reason: 'selectItems'};
      for (const group of this.choiceGroups) {
        const chosen = selections.filter(selected => group.itemKeys.includes(selected.key));
        if (chosen.length < group.min || chosen.length > group.max) return {valid: false, reason: 'chooseModel', key: chosen[0]?.key};
      }
      const seen = new Set();
      const normalized = [];
      let total = 0;
      for (const selected of selections) {
        const item = this.items.find(value => value.key === selected.key);
        if (!item || seen.has(item.key)) return {valid: false, reason: 'chooseVariant', key: selected.key};
        seen.add(item.key);
        const variant = this.variant(item.key, selected.variantId);
        if (!variant) return {valid: false, reason: 'chooseVariant', key: item.key};
        if (!variant.available) return {valid: false, reason: 'unavailable', key: item.key};
        if (!Number.isSafeInteger(selected.quantity) || selected.quantity < 1) return {valid: false, reason: 'invalidQuantity', key: item.key};
        total += variant.price * selected.quantity;
        if (!Number.isSafeInteger(total)) return {valid: false, reason: 'invalidQuantity', key: item.key};
        const variantTitle = variant.title === 'Default Title' ? this.config.strings?.defaultVariant || variant.title : variant.title;
        normalized.push({key: item.key, id: variant.id, quantity: selected.quantity, price: variant.price, title: item.title, variantTitle});
      }
      for (const rule of this.rules) {
        const left = normalized.find(value => value.key === rule.left);
        const right = normalized.find(value => value.key === rule.right);
        if (left && right && !rule.pairs.some(pair => String(pair[0]) === left.id && String(pair[1]) === right.id)) return {valid: false, reason: 'incompatible', key: right.key};
      }
      return {valid: true, items: normalized, total};
    }

    /** @param {Array<object>} selections @returns {Promise<object>} */
    async add(selections) {
      if (this.busy || this.locked || this.destroyed) return {kind: 'locked'};
      const chosen = this.validate(selections);
      if (!chosen.valid) return {kind: 'invalid', ...chosen};
      const snapshot = chosen.items.map(value => Object.freeze({...value}));
      this.busy = true;
      this.setState('preparing');
      try {
        await this.refreshProducts(this.behaviorVersion === 2 || this.choiceGroups.length ? snapshot.map(value => value.key) : undefined);
        const fresh = this.validate(snapshot.map(value => ({key: value.key, variantId: value.id, quantity: value.quantity})));
        if (!fresh.valid) {
          this.setState('ready');
          return {kind: 'invalid', ...fresh};
        }
        if (fresh.items.some((value, index) => value.price !== snapshot[index].price)) {
          this.setState('ready');
          return {kind: 'priceChanged'};
        }
        const before = await this.readCart();
        const token = this.makeActionId();
        if (typeof token !== 'string' || !/^[A-Za-z0-9_-]{12,128}$/.test(token) || this.usedActionIds.has(token) || before.items.some(line => line.properties?._maisonvera_room_action === token)) throw new Error('Action identity collision');
        this.usedActionIds.add(token);
        const combined = new Map();
        for (const item of fresh.items) {
          const previous = combined.get(item.id);
          if (previous) previous.quantity += item.quantity;
          else combined.set(item.id, {...item});
        }
        const expected = Array.from(combined.values(), value => Object.freeze({...value}));
        this.action = Object.freeze({token, room: this.config.key, expected: Object.freeze(expected)});
        const items = expected.map(value => ({id: value.id, quantity: value.quantity, properties: {_maisonvera_room_action: token, _maisonvera_room: this.config.key}}));
        this.setState('sending');
        let status = null;
        try {
          const response = await this.request(this.routes.cartAdd, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({items})});
          status = response.status;
        } catch (_) {
          // A rejected/aborted request can already have modified the server cart.
        }
        return await this.resolveAction(status);
      } catch (_) {
        if (this.action) {
          this.setState('unknown');
          return {kind: 'unknown'};
        }
        this.setState('ready');
        return {kind: 'readFailed'};
      } finally {
        this.busy = false;
      }
    }

    /** @param {number|null} responseStatus @returns {Promise<object>} */
    async resolveAction(responseStatus) {
      try {
        const proof = reconcile(this.action, await this.readCart());
        if (proof.exact) {
          this.setState('confirmed');
          return {kind: 'confirmed', ...proof};
        }
        if (proof.unexpected) {
          this.setState('unknown');
          return {kind: 'unknown', ...proof};
        }
        if (proof.added) {
          this.setState('partial');
          return {kind: 'partial', ...proof};
        }
        // A completed validation rejection plus a real empty action readback is
        // a known no-write outcome. A timeout/5xx/odd success is not that proof.
        if (responseStatus === 422) {
          this.action = null;
          this.setState('ready');
          return {kind: 'failed', ...proof};
        }
        this.setState('unknown');
        return {kind: 'unknown', ...proof};
      } catch (_) {
        this.setState('unknown');
        return {kind: 'unknown'};
      }
    }

    /** Read only: never resubmit the uncertain action. @returns {Promise<object>} */
    async recheck() {
      if (this.busy || !this.action || this.destroyed) return {kind: 'locked'};
      this.busy = true;
      this.setState('checking');
      try {
        return await this.resolveAction(null);
      } finally {
        this.busy = false;
      }
    }

    destroy() {
      this.destroyed = true;
      for (const controller of this.controllers) controller.abort();
      this.controllers.clear();
    }
  }

  /** @param {object} browser @returns {typeof HTMLElement} */
  function createElementClass(browser) {
    return class MaisonveraRoom extends browser.HTMLElement {
      #engine = null;
      #listeners = null;
      #config = null;
      #rows = new Map();
      #groups = new Map();
      #ready = false;
      #message = '';

      connectedCallback() {
        if (this.#engine) return;
        try {
          this.#config = JSON.parse(this.querySelector('[data-mv-room-data]').textContent);
          for (const row of this.querySelectorAll('[data-room-item]')) this.#rows.set(row.dataset.key, row);
          this.#listeners = new AbortController();
          const options = {signal: this.#listeners.signal};
          this.#engine = new RoomCartEngine(this.#config, {origin: browser.location.origin, fetch: browser.fetch.bind(browser), makeActionId: () => actionId(browser.crypto), onState: () => this.#render()});
          for (const item of this.#engine.items) {
            const row = this.#rows.get(item.key);
            if (!row || !row.querySelector('[data-room-select]') || !row.querySelector('[data-room-variant]') || !row.querySelector('[data-room-quantity]')) throw new Error('Room controls missing');
          }
          for (const group of this.#engine.choiceGroups) {
            const fieldset = this.querySelector('[data-room-choice-group="' + group.key + '"]');
            const radios = Array.from(fieldset?.querySelectorAll('[data-room-model]') || []);
            const expected = ['', ...group.itemKeys];
            if (radios.length !== expected.length || expected.some(value => radios.filter(radio => radio.value === value).length !== 1)) throw new Error('Room model controls missing');
            this.#groups.set(group.key, {group, fieldset, radios, selected: ''});
          }
          this.addEventListener('change', event => this.#change(event), options);
          this.addEventListener('input', () => this.#render(), options);
          this.querySelector('[data-room-add]')?.addEventListener('click', event => {
            event.preventDefault();
            void this.#add();
          }, options);
          this.querySelector('[data-room-recheck]')?.addEventListener('click', () => {
            void this.#recheck();
          }, options);
          const native = browser.document.querySelector('.product-section[data-product-id="' + id(this.#config.productId) + '"]:not([data-modal="true"])');
          native?.addEventListener('variantChange', event => this.#nativeVariant(event.detail?.variant, true), {...options, capture: true});
          browser.document.addEventListener('variant:change', event => this.#nativeVariant(event.detail?.variant, false), options);
          this.dataset.roomEnhanced = 'true';
          void this.#initialize(native);
        } catch (_) {
          this.#engine?.destroy();
          this.#listeners?.abort();
          this.#engine = null;
          this.querySelector('[data-room-add]')?.setAttribute('disabled', '');
        }
      }

      disconnectedCallback() {
        this.#listeners?.abort();
        this.#engine?.destroy();
        this.#listeners = null;
        this.#engine = null;
        this.#ready = false;
        this.#rows.clear();
        this.#groups.clear();
      }

      #string(key) {
        return this.#config?.strings?.[key] || '';
      }

      #money(amount) {
        return new Intl.NumberFormat(this.#config.locale, {style: 'currency', currency: this.#config.currency}).format(amount / 100);
      }

      #selection() {
        const result = [];
        for (const [key, row] of this.#rows) {
          if (!row.querySelector('[data-room-select]').checked) continue;
          result.push({key, variantId: row.querySelector('[data-room-variant]').value, quantity: Number(row.querySelector('[data-room-quantity]').value)});
        }
        return result;
      }

      /** Handle an explicit model selection. @param {Event} event @returns {void} */
      #change(event) {
        for (const state of this.#groups.values()) {
          const radio = state.radios.find(value => value === event.target);
          const member = state.group.itemKeys.find(key => this.#rows.get(key).querySelector('[data-room-select]') === event.target);
          if (!radio && !member) continue;
          if (this.#ready && !this.#engine.busy && !this.#engine.locked) {
            state.selected = radio ? radio.value : event.target.checked ? member : '';
            this.#message = '';
          }
        }
        this.#render();
      }

      /** Rebuild a native variant picker without choosing a replacement. @param {object} item @param {object[]} variants @returns {void} */
      #options(item, variants) {
        const select = this.#rows.get(item.key).querySelector('[data-room-variant]');
        const previous = select.value;
        select.replaceChildren();
        const placeholder = browser.document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.#string('chooseVariant');
        select.append(placeholder);
        for (const variant of variants) {
          const option = browser.document.createElement('option');
          option.value = variant.id;
          const title = variant.title === 'Default Title' ? this.#string('defaultVariant') : variant.title;
          option.textContent = title + ' — ' + this.#money(variant.price) + (variant.available ? '' : ' — ' + this.#string('unavailable'));
          option.disabled = !variant.available;
          select.append(option);
        }
        select.value = variants.some(value => value.id === previous) ? previous : '';
      }

      #updateOptions() {
        for (const item of this.#engine.items) {
          if (!this.#engine.groupForItem.has(item.key)) this.#options(item, this.#engine.products.get(item.key)?.variants || []);
        }
        this.#modelOptions();
      }

      /** Refresh model options for the currently selected anchor variant. @returns {void} */
      #modelOptions() {
        for (const {group} of this.#groups.values()) {
          const anchorId = this.#rows.get(group.anchor).querySelector('[data-room-variant]').value;
          for (const key of group.itemKeys) this.#options(this.#engine.items.find(item => item.key === key), this.#engine.matchingVariants(key, anchorId));
        }
      }

      #nativeVariant(variant, scoped) {
        if (!this.#ready || this.#engine.busy || this.#engine.locked) return;
        const main = this.#engine.items.find(item => item.productId === String(this.#config.productId));
        if (!main) return;
        if (!scoped && !this.#engine.variant(main.key, String(variant?.id))) return;
        const select = this.#rows.get(main.key).querySelector('[data-room-variant]');
        select.value = this.#engine.variant(main.key, String(variant?.id)) ? String(variant.id) : '';
        this.#message = '';
        this.#render();
      }

      async #initialize(native) {
        const engine = this.#engine;
        const firstInitialization = !this.#ready;
        const result = await engine.initialize();
        if (this.#engine !== engine || !this.isConnected) return;
        this.#ready = result.kind === 'ready';
        if (this.#ready) {
          this.#updateOptions();
          const value = native?.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
          if (firstInitialization && value) this.#nativeVariant({id: value}, true);
        }
        this.#feedback(result);
        this.#render();
      }

      async #add() {
        if (!this.#ready || !this.#engine || this.#engine.busy || this.#engine.locked) return;
        const engine = this.#engine;
        const result = await engine.add(this.#selection());
        if (this.#engine !== engine || !this.isConnected) return;
        this.#updateOptions();
        this.#feedback(result);
        this.#render();
      }

      async #recheck() {
        if (!this.#engine || this.#engine.busy) return;
        if (!this.#engine.action) {
          const native = browser.document.querySelector('.product-section[data-product-id="' + id(this.#config.productId) + '"]:not([data-modal="true"])');
          await this.#initialize(native);
          return;
        }
        const engine = this.#engine;
        const result = await engine.recheck();
        if (this.#engine !== engine || !this.isConnected) return;
        this.#feedback(result);
        this.#render();
      }

      #feedback(result) {
        const keys = {readFailed: 'cartFailed', priceChanged: 'priceChanged', failed: 'cartFailed', partial: 'cartPartial', unknown: 'cartUnknown', confirmed: 'added'};
        this.#message = this.#string(result.reason || keys[result.kind]);
        if (result.missing?.length) {
          for (const item of result.missing) this.#message += '\n' + item.title + ' — ' + item.variantTitle + ' × ' + item.quantity;
        }
        const recovery = this.querySelector('[data-room-recheck]');
        if (recovery) recovery.hidden = !['readFailed', 'unknown', 'partial'].includes(result.kind);
        const link = this.querySelector('[data-room-cart-link]');
        if (link) link.hidden = !['readFailed', 'unknown', 'partial', 'failed'].includes(result.kind);
        if (result.kind === 'confirmed') browser.location.assign(this.#engine.routes.cartPage.href);
      }

      #render() {
        if (!this.#engine) return;
        const pending = this.#engine.busy;
        const locked = this.#engine.locked;
        if (this.#ready && !pending && !locked) this.#modelOptions();
        for (const {group, fieldset, radios, selected} of this.#groups.values()) {
          fieldset.disabled = pending || locked || !this.#ready;
          const anchorId = this.#rows.get(group.anchor).querySelector('[data-room-variant]').value;
          for (const radio of radios) {
            radio.checked = radio.value === selected;
            const variants = radio.value ? this.#engine.matchingVariants(radio.value, anchorId) : [];
            const available = variants.filter(variant => variant.available);
            radio.disabled = pending || locked || !this.#ready || Boolean(radio.value && !available.length);
            if (!radio.value) continue;
            const row = this.#rows.get(radio.value);
            row.hidden = radio.value !== selected;
            row.querySelector('[data-room-select]').checked = radio.value === selected;
            const price = fieldset.querySelector('[data-room-model-price="' + radio.value + '"]');
            if (price) price.textContent = this.#ready && available.length ? this.#money(Math.min(...available.map(variant => variant.price))) : '—';
            const status = fieldset.querySelector('[data-room-model-status="' + radio.value + '"]');
            if (status) status.textContent = this.#ready && !available.length ? this.#string(this.#engine.products.has(radio.value) && !variants.length ? 'incompatibleModel' : 'modelUnavailable') : '';
          }
        }
        const selection = this.#selection();
        const validation = this.#ready ? this.#engine.validate(selection) : {valid: false};
        this.setAttribute('aria-busy', String(pending));
        for (const item of this.#engine.items) {
          const row = this.#rows.get(item.key);
          if (!row) continue;
          const checkbox = row.querySelector('[data-room-select]');
          const select = row.querySelector('[data-room-variant]');
          const quantity = row.querySelector('[data-room-quantity]');
          checkbox.disabled = pending || locked;
          select.disabled = pending || locked || !checkbox.checked;
          quantity.disabled = pending || locked || !checkbox.checked;
          const invalid = checkbox.checked && validation.key === item.key;
          select.setAttribute('aria-invalid', String(invalid && validation.reason !== 'invalidQuantity'));
          quantity.setAttribute('aria-invalid', String(invalid && validation.reason === 'invalidQuantity'));
          const status = row.querySelector('[data-room-item-status]');
          if (status) status.textContent = invalid ? this.#string(validation.reason) : '';
          const variant = this.#engine.variant(item.key, select.value);
          const price = row.querySelector('[data-room-price]');
          const linePrice = variant ? variant.price * Number(quantity.value) : NaN;
          if (price) price.textContent = Number.isSafeInteger(linePrice) && Number(quantity.value) > 0 ? this.#money(linePrice) : '—';
          const image = row.querySelector('[data-room-image]');
          // Product Ajax can contain an old stamped source. Only use image URLs
          // passed through the theme's reviewed image mapping, never raw Ajax media.
          const mappedVariant = item.variants?.find(value => String(value.id) === select.value);
          const source = typeof mappedVariant?.image === 'string' ? mappedVariant.image : mappedVariant?.image?.src;
          if (image && typeof source === 'string') {
            const url = new URL(source, browser.location.origin);
            if (['https:', 'http:'].includes(url.protocol)) {
              image.src = url.href;
              image.removeAttribute('srcset');
              image.alt = mappedVariant?.image?.alt || mappedVariant?.imageAlt || item.title;
            }
          }
          const imageLink = row.querySelector('[data-room-image-link]');
          if (imageLink && item.url) {
            const url = localUrl(item.url, browser.location.origin);
            if (variant) url.searchParams.set('variant', variant.id);
            imageLink.href = url.href;
          }
        }
        const total = this.querySelector('[data-room-total]');
        if (total) total.textContent = validation.valid ? this.#money(validation.total) : '—';
        const button = this.querySelector('[data-room-add]');
        if (button) {
          button.disabled = pending || locked || !this.#ready || !validation.valid;
          button.textContent = this.#string(pending ? 'loading' : 'addSelected');
        }
        const recovery = this.querySelector('[data-room-recheck]');
        if (recovery) recovery.disabled = pending;
        const message = this.querySelector('[data-room-message]');
        if (message) message.textContent = pending ? this.#string('loading') : this.#message || (!validation.valid && this.#ready ? this.#string(validation.reason) : '');
      }
    };
  }

  if (typeof module === 'object' && module.exports) module.exports = {RoomCartEngine, checkedCart, reconcile, localUrl, createElementClass};
  if (host.customElements && host.HTMLElement && !host.customElements.get('maisonvera-room')) host.customElements.define('maisonvera-room', createElementClass(host));
})(typeof window === 'undefined' ? globalThis : window);
